<!-- SkyPlus Indexing Queue Component Template -->
<!-- Location: app/src/app/components/skyplus/indexing/indexing-queue.component.html -->

<div class="indexing-container">
  
  <!-- Header -->
  <div class="indexing-header">
    <div class="header-left">
      <button class="back-btn" (click)="cancelIndexing()">
        <span class="material-icons">arrow_back</span>
      </button>
      <h1>{{ isEditMode ? 'Edit Document Metadata' : 'Index Documents' }}</h1>
      <span class="file-counter" *ngIf="!isEditMode && queuedFiles.length > 0">
        {{ currentIndex + 1 }} of {{ queuedFiles.length }}
        <span class="completed-badge" *ngIf="completedCount > 0">({{ completedCount }} saved)</span>
      </span>
    </div>
    <div class="header-right">
      <button class="btn-secondary" (click)="cancelIndexing()">Cancel</button>
      <button class="btn-primary" 
              *ngIf="!isEditMode"
              [disabled]="!hasFiles || isSaving"
              (click)="saveAllFiles()">
        <span class="material-icons" *ngIf="isSaving">sync</span>
        Save All
      </button>
    </div>
  </div>
  
  <!-- Main Content -->
  <div class="indexing-content" *ngIf="hasFiles">
    
    <!-- Left Panel: File Preview -->
    <div class="preview-panel">
      <div class="preview-header">
        <span class="material-icons">preview</span>
        <span>Document Preview</span>
      </div>
      
      <div class="file-info" *ngIf="currentFile">
        <div class="file-name">
          <span class="material-icons">description</span>
          {{ currentFile.file.name }}
        </div>
        <div class="file-meta">
          <span>{{ formatFileSize(currentFile.file.size) }}</span>
          <span class="status-badge" [class]="currentFile.status">{{ currentFile.status }}</span>
        </div>
      </div>
      
      <div class="preview-content">
        <ng-container *ngIf="currentFile">
          <img *ngIf="currentFile.previewType === 'image'" [src]="currentFile.previewUrl" class="preview-image" alt="Preview">
          <iframe *ngIf="currentFile.previewType === 'pdf'" [src]="currentFile.previewUrl" class="preview-pdf" frameborder="0"></iframe>
          <div *ngIf="currentFile.previewType === 'text'" class="preview-text"><p>Text preview not available yet</p></div>
          <div *ngIf="currentFile.previewType === 'other'" class="preview-unavailable">
            <span class="material-icons">visibility_off</span>
            <p>Preview not available for this file type</p>
          </div>
        </ng-container>
        <div *ngIf="isEditMode && editingNode" class="edit-mode-preview">
          <iframe *ngIf="editPreviewType === 'pdf' && editPreviewUrl"
                  [src]="editPreviewUrl"
                  style="width:100%;height:100%;flex:1;border:none;border-radius:4px;"></iframe>
          <img *ngIf="editPreviewType === 'image' && editPreviewObjectUrl"
               [src]="editPreviewObjectUrl"
               style="max-width:100%;max-height:100%;object-fit:contain;" />
          <ng-container *ngIf="editPreviewType === 'other' || !editPreviewUrl">
            <span class="material-icons large-icon">description</span>
            <p>{{ editingNode.name }}</p>
          </ng-container>
        </div>
      </div>
      
      <div class="preview-navigation" *ngIf="!isEditMode && queuedFiles.length > 1">
        <button class="nav-btn" [disabled]="!canGoPrevious" (click)="goToPrevious()">
          <span class="material-icons">chevron_left</span> Previous
        </button>
        <div class="file-dots">
          <span *ngFor="let f of queuedFiles; let i = index" class="dot" 
                [class.active]="i === currentIndex"
                [class.completed]="f.status === 'completed'"
                [class.error]="f.status === 'error'"
                (click)="goToFile(i)"></span>
        </div>
        <button class="nav-btn" [disabled]="!canGoNext" (click)="goToNext()">
          Next <span class="material-icons">chevron_right</span>
        </button>
      </div>
    </div>
    
    <!-- Right Panel: Metadata Form -->
    <div class="metadata-panel">
      <div class="metadata-header">
        <span class="material-icons">edit_note</span>
        <span>Document Metadata</span>
      </div>
      
      <div class="metadata-form">
        
        <!-- Document Type Selector -->
        <div class="form-section">
          <label class="section-label">Document Type</label>
          <div class="document-type-selector">
            <button class="type-dropdown-btn" (click)="showDocumentTypeDropdown = !showDocumentTypeDropdown">
              <span class="material-icons" *ngIf="selectedDocumentType" [style.color]="selectedDocumentType.color">{{ selectedDocumentType.icon }}</span>
              <span class="material-icons" *ngIf="!selectedDocumentType">category</span>
              <span>{{ selectedDocumentType?.name || 'Select Document Type (Optional)' }}</span>
              <span class="material-icons dropdown-arrow">expand_more</span>
            </button>
            
            <div class="type-dropdown" *ngIf="showDocumentTypeDropdown">
              <div class="type-option" (click)="selectDocumentType(null)">
                <span class="material-icons">description</span>
                <div class="type-info">
                  <span class="type-name">Default (No Custom Metadata)</span>
                  <span class="type-desc">Upload without custom indexing</span>
                </div>
              </div>
              <div class="type-option" *ngFor="let dt of documentTypes" (click)="selectDocumentType(dt)">
                <span class="material-icons" [style.color]="dt.color">{{ dt.icon }}</span>
                <div class="type-info">
                  <span class="type-name">{{ dt.name }}</span>
                  <span class="type-desc">{{ dt.description }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Target Folder Section -->
        <div class="form-section">
          <label class="section-label">Storage Location</label>
          
          <!-- Round 7 — items 7/8: a document type with a configured
               destination locks the location (no re-picking); the escape
               hatch unlocks the browser for the rare exception. -->
          <div class="folder-selector">
            <button class="folder-btn" (click)="destinationLocked ? null : openFolderBrowser()"
                    [style.opacity]="destinationLocked ? 0.75 : 1"
                    [style.cursor]="destinationLocked ? 'default' : 'pointer'">
              <span class="material-icons">{{ destinationLocked ? 'lock' : 'folder' }}</span>
              <span class="folder-path">{{ selectedTargetFolder?.path || selectedTargetFolder?.name || 'Select Target Folder' }}</span>
              <span class="material-icons" *ngIf="!destinationLocked">chevron_right</span>
            </button>
            <button type="button" class="folder-btn" *ngIf="destinationLocked"
                    style="flex:none; width:auto; margin-top:6px"
                    (click)="unlockDestination()">
              <span class="material-icons">edit_location_alt</span> Change location
            </button>
          </div>
          
          <!-- MAIN Folder Creation -->
          <!-- Round 7 — rule-driven path preview. When the document type
               defines folder rules, the engine owns the structure: the
               legacy folder/subfolder pickers are hidden and this preview
               shows exactly where the document will land. -->
          <div class="folder-path-preview" *ngIf="docTypeHasRules()">
            <span class="material-icons">account_tree</span>
            <span>
              {{ selectedTargetFolder?.name || 'destination' }} /
              <strong>{{ rulePathPreview || '…' }}</strong>
              <em *ngIf="rulePathFolio"> · folio {{ rulePathFolio }}</em>
            </span>
          </div>
          <p class="folder-hint" *ngFor="let e of rulePathErrors" style="color:#b3261e"
             [hidden]="!docTypeHasRules()">{{ e }}</p>

          <div class="folder-creation-option" *ngIf="!docTypeHasRules() && folderCreationFields.length > 0">
            <label class="checkbox-label">
              <input type="checkbox" [checked]="createFolderFromField !== null"
                     (change)="onMainFolderCheckboxChange($any($event.target).checked)">
              <span>Create folder from field value</span>
            </label>
            <select *ngIf="createFolderFromField !== null" [(ngModel)]="createFolderFromField" class="folder-field-select">
              <option *ngFor="let field of folderCreationFields" [value]="field.propertyName">{{ field.label }}</option>
            </select>
            <p class="folder-hint" *ngIf="createFolderFromField && formValues[createFolderFromField]">
              Will create folder: <strong>{{ formValues[createFolderFromField] }}</strong>
            </p>
          </div>
          
          <!-- NEW: SUBFOLDER Creation -->
          <div class="subfolder-creation-option" *ngIf="!docTypeHasRules() && subfolderCreationFields.length > 0 && createFolderFromField !== null">
            <label class="checkbox-label">
              <input type="checkbox" [checked]="createSubfolderFromField !== null"
                     (change)="onSubfolderCheckboxChange($any($event.target).checked)">
              <span>Create subfolder from field value</span>
            </label>
            <select *ngIf="createSubfolderFromField !== null" [(ngModel)]="createSubfolderFromField" class="folder-field-select">
              <option *ngFor="let field of subfolderCreationFields" [value]="field.propertyName">{{ field.label }}</option>
            </select>
            <p class="folder-hint" *ngIf="createSubfolderFromField && formValues[createSubfolderFromField]">
              Will create subfolder: <strong>{{ getSubfolderPreview(getSubfolderField(), formValues[createSubfolderFromField]) }}</strong>
            </p>
          </div>
          
          <!-- NEW: Folder Path Preview -->
          <div class="folder-path-preview" *ngIf="selectedTargetFolder && (createFolderFromField || createSubfolderFromField)">
            <span class="material-icons">folder_open</span>
            <span class="preview-path">
              {{ selectedTargetFolder.name }}/
              <strong *ngIf="createFolderFromField && formValues[createFolderFromField]">{{ formValues[createFolderFromField] }}/</strong>
              <strong class="subfolder-name" *ngIf="createSubfolderFromField && formValues[createSubfolderFromField]">{{ getSubfolderPreview(getSubfolderField(), formValues[createSubfolderFromField]) }}/</strong>
              <em>{{ currentFile?.file?.name || 'document.pdf' }}</em>
            </span>
          </div>
        </div>
        
        <!-- Dynamic Metadata Fields -->
        <div class="form-section" *ngIf="selectedDocumentType">
          <label class="section-label">{{ selectedDocumentType.name }} Properties</label>
          <div class="metadata-fields">
            <div class="form-field" *ngFor="let field of selectedDocumentType.fields">
              <label [class.required]="field.mandatory">
                {{ field.label }}<span class="required-marker" *ngIf="field.mandatory">*</span>
              </label>
              <input *ngIf="field.type === 'text'" type="text" [placeholder]="field.placeholder || ''"
                     [value]="formValues[field.propertyName] || ''"
                     (input)="onFieldChange(field.propertyName, $any($event.target).value)"
                     [class.error]="formErrors[field.propertyName]" [readonly]="field.autoPopulateUserId">
              <input *ngIf="field.type === 'number'" type="number" [placeholder]="field.placeholder || ''"
                     [value]="formValues[field.propertyName] || ''"
                     (input)="onFieldChange(field.propertyName, $any($event.target).value)"
                     [class.error]="formErrors[field.propertyName]">
              <input *ngIf="field.type === 'date'" type="date" [value]="formValues[field.propertyName] || ''"
                     (input)="onFieldChange(field.propertyName, $any($event.target).value)"
                     [class.error]="formErrors[field.propertyName]">
              <select *ngIf="field.type === 'select'" [value]="formValues[field.propertyName] || ''"
                      (change)="onFieldChange(field.propertyName, $any($event.target).value)"
                      [class.error]="formErrors[field.propertyName]">
                <option value="">-- Select --</option>
                <option *ngFor="let opt of field.options" [value]="opt.value">{{ opt.label }}</option>
              </select>
              <textarea *ngIf="field.type === 'textarea'" [placeholder]="field.placeholder || ''"
                        [value]="formValues[field.propertyName] || ''"
                        (input)="onFieldChange(field.propertyName, $any($event.target).value)"
                        [class.error]="formErrors[field.propertyName]" rows="3"></textarea>
              <span class="field-error" *ngIf="formErrors[field.propertyName]">{{ formErrors[field.propertyName] }}</span>
            </div>
          </div>
        </div>
        
        <div class="no-type-message" *ngIf="!selectedDocumentType">
          <span class="material-icons">info</span>
          <p>No document type selected. The file will be uploaded with default Alfresco metadata only.</p>
        </div>
        
        <!-- Action Buttons -->
        <div class="form-actions">
          <button class="btn-secondary" *ngIf="!isEditMode" (click)="skipCurrentFile()" [disabled]="!canGoNext">Skip</button>
          <button class="btn-secondary" *ngIf="!isEditMode" (click)="removeCurrentFile()">
            <span class="material-icons">delete</span> Remove
          </button>
          <button class="btn-primary" *ngIf="!isEditMode" [disabled]="isSaving || !selectedTargetFolder" (click)="saveCurrentFile()">
            <span class="material-icons" *ngIf="isSaving">sync</span>
            <span class="material-icons" *ngIf="!isSaving">save</span>
            {{ isSaving ? 'Saving...' : 'Save & Continue' }}
          </button>
          <button class="btn-primary" *ngIf="isEditMode" [disabled]="isSaving" (click)="saveEditedMetadata()">
            <span class="material-icons" *ngIf="isSaving">sync</span>
            <span class="material-icons" *ngIf="!isSaving">save</span>
            {{ isSaving ? 'Saving...' : 'Update Metadata' }}
          </button>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Empty State -->
  <div class="empty-state" *ngIf="!hasFiles && !isLoading">
    <div class="empty-content">
      <span class="material-icons">cloud_upload</span>
      <h2>Upload Documents for Indexing</h2>
      <p>Select one or more documents to upload and index with custom metadata.</p>
      <button class="btn-primary large" (click)="triggerFileSelect()">
        <span class="material-icons">add</span> Select Files
      </button>
    </div>
  </div>
  
  <div class="loading-overlay" *ngIf="isLoading"><div class="spinner"></div><p>Loading...</p></div>
  
  <!-- Folder Browser Modal -->
  <div class="modal-overlay" *ngIf="showFolderBrowser" (click)="closeFolderBrowser()">
    <div class="folder-browser-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Select Target Folder</h3>
        <button class="close-btn" (click)="closeFolderBrowser()"><span class="material-icons">close</span></button>
      </div>
      <div class="folder-breadcrumbs">
        <span *ngFor="let crumb of folderBreadcrumbs; let last = last" class="breadcrumb-item"
              [class.clickable]="!last" (click)="!last && navigateToFolder(crumb.id)">
          {{ crumb.name }}<span class="material-icons" *ngIf="!last">chevron_right</span>
        </span>
      </div>
      <div class="folder-list">
        <div class="folder-item" *ngFor="let folder of browseFolderContents" (dblclick)="navigateToFolder(folder.id)">
          <span class="material-icons folder-icon">folder</span>
          <span class="folder-name">{{ folder.name }}</span>
          <button class="select-folder-btn" (click)="selectFolder(folder)">Select</button>
        </div>
        <div class="no-folders" *ngIf="browseFolderContents.length === 0 && !isLoading">
          <span class="material-icons">folder_off</span><p>No subfolders in this location</p>
        </div>
      </div>
      <div class="modal-actions">
        <button class="btn-secondary" (click)="closeFolderBrowser()">Cancel</button>
        <button class="btn-primary" (click)="selectCurrentFolder()">
          <span class="material-icons">check</span> Use This Folder
        </button>
      </div>
    </div>
  </div>
</div>